import { useEffect, useRef } from "react";
import { ProductLists } from "../components/lists/ProductLists";
import { Link, useLocation, useParams, useSearchParams } from "react-router";
import type { ProductDetail as ProductDetailDTO } from "../../shared/types";
import { useNative, useRail } from "../../shared/phone/media";
import { Split } from "../../shared/phone/Split";
import { cpr } from "../lib/format";
import type { ProductGoal } from "../lib/productsApi";
import { AssetLibrary } from "../components/AssetLibrary";
import { BackLink } from "../components/ui/BackLink";
import { Icon } from "../components/ui/Icon";
import { AssetsNative } from "./products/AssetsNative";
import { ProductMark } from "./products/ProductMark";
import { ProductScreen, ProductsList } from "./products/ProductsNative";
import { ProfileCard } from "./products/ProfileCard";
import { goalLine, goalPill, TRACKING } from "./products/status";
import { plural } from "./products/text";
import { useProductPage } from "./products/useProductPage";
import { useProducts } from "./products/useProducts";
import { useToast } from "./products/useToast";
import "../styles/product.css";
import { NewGoalMenu } from "../components/NewGoalMenu";
import { Can, useCan } from "../context/AuthContext";

function Goals({ goals }: { goals: ProductGoal[] }) {
  return (
    <section className="card pd-sec">
      <div className="card-h">
        <h3>Goals</h3>
      </div>
      {goals.length ? (
        <div className="pd-goals">
          {goals.map((g) => {
            const { title, sub } = goalLine(g);
            const [cls, label] = goalPill(g.state);
            return (
              <Link className="pd-goal" key={g.id} to={`/goals/${encodeURIComponent(g.id)}`}>
                <div>
                  <b>{title}</b>
                  {sub && <span>{sub}</span>}
                </div>
                <span className={cls ? `pill ${cls}` : "pill"}>{label}</span>
                <Icon name="right" />
              </Link>
            );
          })}
        </div>
      ) : (
        <p className="pd-none">No goals yet.</p>
      )}
    </section>
  );
}

/** Where each goal's Meta ads open (notes/AUDIENCE.md 8.0): the product keeps one dataset; each goal checks its own page. */
function WhereOpens({ goals }: { goals: ProductGoal[] }) {
  const can = useCan();
  const meta = goals.filter((g) => g.opens && g.state !== "ended");
  if (!meta.length) return null;
  return (
    <section className="card pd-sec">
      <div className="card-h">
        <h3>Where each goal's ads open</h3>
      </div>
      <div className="pd-goals">
        {meta.map((g) => {
          const o = g.opens!;
          const ok = o.tracking !== "not";
          const row = (
            <>
              <div>
                <b>{goalLine(g).title}</b>
                <span>{o.opens === "metaform" ? "Meta's form" : (o.page ?? "No page yet")}</span>
              </div>
              <span className={ok ? "pill good" : "pill tight"}>{TRACKING[o.tracking]}</span>
            </>
          );
          return can("editor") ? (
            <Link className="pd-goal" key={g.id} to={`/goals/${encodeURIComponent(g.id)}/ads`}>
              {row}
              <Icon name="right" />
            </Link>
          ) : (
            <div className="pd-goal" key={g.id}>
              {row}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Partners({ id, lane }: { id: string; lane: ProductDetailDTO["partnersLane"] }) {
  const any = lane.partners > 0 || lane.sales > 0;
  return (
    <section className="card pd-sec">
      <div className="card-h">
        <h3>Partners</h3>
      </div>
      <div className="pd-row">
        <span className="ki">
          <Icon name="users" />
        </span>
        <div>
          <b>Affiliates promote it with a link</b>
          <span>
            {any
              ? `${plural(lane.partners, "active partner")} · ${plural(lane.sales, "sale")}${lane.sales > 0 ? ` · ${cpr(lane.costPerSaleCents)} per sale` : ""}`
              : "No partners yet. Invite affiliates to promote this product with a link."}
          </span>
        </div>
        <Can min="admin">
          <Link className="btn sm ghost" to={`/products/${encodeURIComponent(id)}/partners`}>
            Manage partners
          </Link>
        </Can>
      </div>
    </section>
  );
}

/** A product on a computer: its goals, its confirmed profile, its lists, Partners and its assets on one page. */
function ProductDetailDesktop({ id }: { id: string }) {
  const { toast, toastNode } = useToast();
  const { data, err, setLogo } = useProductPage(id, toast);
  const fileRef = useRef<HTMLInputElement>(null);
  const { hash } = useLocation();
  const can = useCan();

  // Why: "Profile" on a product card lands on this page's profile card.
  useEffect(() => {
    if (data && hash) document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [data, hash]);

  if (err)
    return (
      <section className="wrap ga">
        <header className="ph">
          <div className="ctx">
            <BackLink to="/products" label="Products" />
          </div>
        </header>
        <p className="load-note">{err.status === 404 ? "This product does not exist, or it belongs to another account." : `Couldn't load this product: ${err.message}`}</p>
      </section>
    );
  if (!data)
    return (
      <section className="wrap ga">
        <p className="load-note">Loading…</p>
      </section>
    );

  const { product, partnersLane } = data.detail;
  const newGoal = `/products/${encodeURIComponent(product.id)}/goals/new`;
  return (
    <section className="wrap ga prodpage">
      <header className="ph">
        <div className="ctx">
          <BackLink to="/products" label="Products" />
        </div>
        <div className="ph-t phead">
          <h1 className="ptitle pd-title">
            <button type="button" className="pd-logo" onClick={() => fileRef.current?.click()} title="Change the logo" aria-label="Change the logo" disabled={!can("editor")}>
              <ProductMark id={product.id} name={product.name} logo={!!product.logoR2Key} />
            </button>
            <span>{product.name}</span>
          </h1>
          <NewGoalMenu to={newGoal} coldEmail={data.detail.coldEmail} label={`New goal for ${product.name}`} />
        </div>
        {product.description && <p className="ph-s">{product.description}</p>}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            void setLogo(file);
          }}
        />
      </header>
      <Goals goals={data.goals} />
      <WhereOpens goals={data.goals} />
      <ProductLists productId={product.id} productName={product.name} />
      <ProfileCard productId={product.id} name={product.name} view={data.profile} />
      <Partners id={product.id} lane={partnersLane} />
      <section className="card pd-sec">
        <div className="card-h">
          <h3>Product assets</h3>
        </div>
        <AssetLibrary productId={product.id} />
      </section>
      {toastNode}
    </section>
  );
}

/**
 * A product on a phone or an iPad. Its Assets and their folders are screens of their own, kept in the
 * address (`?assets`, `?assets&folder=`). On an iPad the product sits beside the Products list.
 */
function ProductDetailNative({ id }: { id: string }) {
  const rail = useRail();
  const [q] = useSearchParams();
  const { items, err } = useProducts();
  if (q.has("assets")) {
    const name = items?.find((p) => p.id === id)?.name;
    return <AssetsNative productId={id} productName={name} folderId={q.get("folder")} />;
  }
  if (!rail) return <ProductScreen id={id} />;
  return <Split list={<ProductsList items={items} err={err} selected={id} />} detail={<ProductScreen id={id} split />} showDetail />;
}

/** A product (`/products/:id`): its goals, its confirmed profile, and the way into Partners. */
export function ProductDetail() {
  const { id = "" } = useParams();
  return useNative() ? <ProductDetailNative id={id} /> : <ProductDetailDesktop id={id} />;
}
